// History Manager
// Handles storing and retrieving purification history

const DEMO_HISTORY_KEY = 'distil-purification-history-demo'; // Demo data (separate!)
const MAX_HISTORY_ITEMS = 1000;

/**
 * Get the localStorage key for a specific wallet address
 */
function getWalletKey(walletAddress) {
  if (!walletAddress) return 'distil-purification-history-unknown';
  return `distil-purification-history-${walletAddress}`;
}

/**
 * Save a purification to history
 * Demo mode saves to a SEPARATE key and is never mixed with real data
 * Real wallet data is scoped to the wallet address
 */
export function savePurification(purificationData, walletAddress = null) {
  // Route demo data to separate storage - never pollutes real history
  const isDemo = purificationData.isDemoMode === true;
  const key = isDemo ? DEMO_HISTORY_KEY : getWalletKey(walletAddress);
  
  const history = isDemo ? getDemoHistory() : getRealHistory(walletAddress);
  
  const entry = {
    id: `purification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: Date.now(),
    date: new Date().toISOString(),
    ...purificationData,
  };

  history.unshift(entry);

  if (history.length > MAX_HISTORY_ITEMS) {
    history.splice(MAX_HISTORY_ITEMS);
  }

  try {
    localStorage.setItem(key, JSON.stringify(history));
    return entry;
  } catch (error) {
    console.error('Failed to save history:', error);
    return null;
  }
}

/**
 * Get REAL wallet purification history for a specific wallet
 */
export function getRealHistory(walletAddress = null) {
  try {
    const key = getWalletKey(walletAddress);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    return [];
  }
}

/**
 * Clear history for a specific wallet address
 */
export function clearWalletHistory(walletAddress) {
  try {
    const key = getWalletKey(walletAddress);
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Get DEMO purification history only
 */
export function getDemoHistory() {
  try {
    const stored = localStorage.getItem(DEMO_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    return [];
  }
}

/**
 * Get history - returns real history only (use this everywhere in the app)
 * Demo history is kept separate and only shown during active demo session
 * Pass walletAddress to get wallet-specific history
 * @returns {Array}
 */
export function getHistory(walletAddress = null) {
  return getRealHistory(walletAddress);
}

/**
 * Clear demo history (called when wallet connects)
 */
export function clearDemoHistory() {
  try {
    localStorage.removeItem(DEMO_HISTORY_KEY);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Get a single purification by ID
 */
export function getPurificationById(id) {
  const history = getHistory();
  return history.find(entry => entry.id === id);
}

/**
 * Get history filtered by date range
 * @param {number} days - Number of days to look back
 */
export function getHistoryByDateRange(days = 7, walletAddress = null) {
  const history = getHistory(walletAddress);
  const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
  
  return history.filter(entry => entry.timestamp >= cutoff);
}

/**
 * Get history filtered by category
 */
export function getHistoryByCategory(categoryId) {
  const history = getHistory();
  
  return history.filter(entry => {
    const breakdown = entry.categoryBreakdown || {};
    return breakdown[categoryId] > 0;
  });
}

/**
 * Calculate lifetime statistics
 */
export function getLifetimeStats(walletAddress = null) {
  const history = getHistory(walletAddress);
  
  const stats = {
    totalPurifications: history.length,
    totalObjectsCleaned: 0,
    totalPurityEarned: 0,
    totalSuiReclaimed: 0,
    totalGasUsed: 0,
    categoryBreakdown: {
      TOXIC: 0,
      SPAM: 0,
      DUST: 0,
    },
    tierUps: 0,
    milestones: [],
    firstPurification: null,
    lastPurification: null,
    averageSuiPerPurification: 0,
    averageObjectsPerPurification: 0,
    biggestCleanup: null,
    mostValuableCleanup: null,
  };

  if (history.length === 0) return stats;

  // Calculate totals
  history.forEach((entry, index) => {
    stats.totalObjectsCleaned += entry.objectCount || 0;
    stats.totalPurityEarned += entry.purityEarned || 0;
    stats.totalSuiReclaimed += entry.suiReclaimed || 0;
    stats.totalGasUsed += entry.gasUsed || 0;

    // Category breakdown
    const breakdown = entry.categoryBreakdown || {};
    stats.categoryBreakdown.TOXIC += breakdown.TOXIC || 0;
    stats.categoryBreakdown.SPAM += breakdown.SPAM || 0;
    stats.categoryBreakdown.DUST += breakdown.DUST || 0;

    // Tier ups
    if (entry.tierUpOccurred) {
      stats.tierUps++;
    }

    // Track biggest cleanup
    if (!stats.biggestCleanup || entry.objectCount > stats.biggestCleanup.objectCount) {
      stats.biggestCleanup = entry;
    }

    // Track most valuable cleanup
    if (!stats.mostValuableCleanup || entry.suiReclaimed > stats.mostValuableCleanup.suiReclaimed) {
      stats.mostValuableCleanup = entry;
    }
  });

  // Dates
  stats.firstPurification = history[history.length - 1];
  stats.lastPurification = history[0];

  // Averages
  stats.averageSuiPerPurification = stats.totalSuiReclaimed / history.length;
  stats.averageObjectsPerPurification = stats.totalObjectsCleaned / history.length;

  // Milestones
  if (history.length >= 1) stats.milestones.push('first_purification');
  if (history.length >= 10) stats.milestones.push('10_purifications');
  if (history.length >= 50) stats.milestones.push('50_purifications');
  if (history.length >= 100) stats.milestones.push('100_purifications');
  if (stats.totalObjectsCleaned >= 100) stats.milestones.push('100_objects_cleaned');
  if (stats.totalObjectsCleaned >= 500) stats.milestones.push('500_objects_cleaned');
  if (stats.totalSuiReclaimed >= 10) stats.milestones.push('10_sui_reclaimed');
  if (stats.totalSuiReclaimed >= 100) stats.milestones.push('100_sui_reclaimed');

  return stats;
}

/**
 * Get statistics for a specific time period
 */
export function getStatsForPeriod(days = 7) {
  const history = getHistoryByDateRange(days);
  
  return {
    purifications: history.length,
    objectsCleaned: history.reduce((sum, e) => sum + (e.objectCount || 0), 0),
    purityEarned: history.reduce((sum, e) => sum + (e.purityEarned || 0), 0),
    suiReclaimed: history.reduce((sum, e) => sum + (e.suiReclaimed || 0), 0),
  };
}

/**
 * Clear all history (careful!)
 */
export function clearHistory(walletAddress = null) {
  try {
    const key = getWalletKey(walletAddress);
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Failed to clear history:', error);
    return false;
  }
}

/**
 * Export history as JSON
 */
export function exportHistoryAsJSON() {
  const history = getHistory();
  const stats = getLifetimeStats();
  
  const exportData = {
    exportDate: new Date().toISOString(),
    version: '1.0',
    stats,
    history,
  };

  return JSON.stringify(exportData, null, 2);
}

/**
 * Export history as CSV
 */
export function exportHistoryAsCSV() {
  const history = getHistory();
  
  const headers = [
    'Date',
    'Time',
    'Objects Cleaned',
    'Purity Earned',
    'SUI Reclaimed',
    'Gas Used',
    'Toxic Count',
    'Spam Count',
    'Dust Count',
    'Tier Up',
    'Transaction Hash',
  ];

  const rows = history.map(entry => {
    const date = new Date(entry.timestamp);
    const breakdown = entry.categoryBreakdown || {};
    
    return [
      date.toLocaleDateString(),
      date.toLocaleTimeString(),
      entry.objectCount || 0,
      entry.purityEarned || 0,
      entry.suiReclaimed || 0,
      entry.gasUsed || 0,
      breakdown.TOXIC || 0,
      breakdown.SPAM || 0,
      breakdown.DUST || 0,
      entry.tierUpOccurred ? 'Yes' : 'No',
      entry.transactionHash || '',
    ];
  });

  const csv = [
    headers.join(','),
    ...rows.map(row => row.join(',')),
  ].join('\n');

  return csv;
}

/**
 * Download history file
 */
export function downloadHistory(format = 'json') {
  const content = format === 'csv' ? exportHistoryAsCSV() : exportHistoryAsJSON();
  const blob = new Blob([content], { type: format === 'csv' ? 'text/csv' : 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `distil-history-${Date.now()}.${format}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Search history
 */
export function searchHistory(query) {
  const history = getHistory();
  const lowerQuery = query.toLowerCase();
  
  return history.filter(entry => {
    // Search in transaction hash
    if (entry.transactionHash?.toLowerCase().includes(lowerQuery)) return true;
    
    // Search in object names
    if (entry.objectsPurified?.some(obj => 
      obj.name?.toLowerCase().includes(lowerQuery)
    )) return true;
    
    // Search in categories
    if (Object.keys(entry.categoryBreakdown || {}).some(cat => 
      cat.toLowerCase().includes(lowerQuery)
    )) return true;
    
    return false;
  });
}
